import * as React from 'react';
import { observer } from 'mobx-react-lite';
import SiteBuilderModel from '../../model/SiteBuilderModel';
import SiteMapModel from '../../../Sitemap/models/SiteMapModel';
import {
  FolderOpenOutlined,
  MoreOutlined,
  LockOutlined
} from '@ant-design/icons';
import { Tooltip, Drawer } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import IframeServiceInjector from '../../services/injectors/IframeServiceInjector';
import PageBuilderDataCollector from '../../services/PageBuilderDataCollector';
import EventsManager from '../../../../services/EventsManager';
import { CHANGE_PAGE_EVENT } from '../../step-events';
import { InfoCircleOutlined } from '@ant-design/icons';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import { BodyText } from '../../../../components/ui/Typography';
import ManageSitemap from '../../../../components/ManageSitemap/ManageSitemap';

function renderTree(tree, parent, level = 0, t) {
  let allRootNodes = tree.filter((node) => node.getParent() === parent);

  // Ensure the Startseite (index) page is always the first root node
  // const startIndex = allRootNodes.findIndex((node) => node.getType() === 'Startseite');
  // if (startIndex > -1) {
  //   const [startNode] = allRootNodes.splice(startIndex, 1);
  //   allRootNodes.unshift(startNode);
  // }

  return allRootNodes.map((node) => {
    let children = renderTree(tree, node.getId(), level + 1, t);
    const isParent = children.length > 0;
    const colorClass =
      node.getType() === 'default'
        ? 'text-[#00000040]'
        : level === 0
        ? 'text-[#000000a6]'
        : 'text-[#000000e0]';
    const isFolderParent = isParent; // Show folder icon for all pages with children

    const basePadding = 24; 
    const indentPerLevel = 24;
    const paddingLeft = basePadding + level * indentPerLevel;
    const parentPadding = isFolderParent
      ? `28px 24px 4px ${paddingLeft}px`
      : `16px 24px 16px ${paddingLeft}px`;

    const isCurrentPage =
      SiteBuilderModel.currentShownInPreviewGeneratedPage.getId() ===
      node.getId();

    const handlePageClick = async () => {
      if (node.getType() === 'default') {
        return;
      }

      const pageBuilderDataCollector =
        IframeServiceInjector.getService<PageBuilderDataCollector>(
          'PageBuilderDataCollector'
        );

      SiteBuilderModel.setIframeLoaded(false);
      SiteBuilderModel.moveToCustomPage(node.getId());

      EventsManager.emit(CHANGE_PAGE_EVENT, {});
    };

    return (
      <div key={node.getId()}>
        {node.getType() === 'default' ? (
          <Tooltip
            title={t(
              'Editing is not available in the preview. After purchase, you can customize this page anytime in the CMS.'
            )}
            placement="bottomRight"
            mouseEnterDelay={0.1}
            overlayClassName="max-w-[260px]"
            getPopupContainer={(trigger) => trigger}
            overlayStyle={{ minWidth: 334 }}>
            <div
              className={`w-full flex items-center gap-2 text-[14px] font-normal relative leading-[22px] ${colorClass} hover:bg-gray-100 cursor-not-allowed`}
              style={{
                borderBottom:
                  '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))',
                padding: parentPadding
              }}>
              {isFolderParent && (
                <FolderOpenOutlined className="w-4 h-4 mr-1" />
              )}
              {!isFolderParent && level > 0 && <MoreOutlined className="w-4 h-4 mr-1" />}
              {node.getPageName()}
              <LockOutlined
                className="w-4 h-4 ml-auto"
                style={{ color: 'rgba(0, 0, 0, 0.25)' }}
              />
            </div>
          </Tooltip>
        ) : (
          <div
            className={`w-full flex items-center gap-2 text-[14px] leading-[22px] ${colorClass} hover:bg-gray-100 cursor-pointer ${
              isCurrentPage ? 'cursor-not-allowed' : 'cursor-pointer'
            } ${isCurrentPage ? 'bg-[#F8F8F8] font-bold' : 'font-normal'}`}
            style={{
              borderBottom: '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))',
              padding: parentPadding,
              color: isCurrentPage ? '#3498DB' : undefined,
              fontWeight: isCurrentPage ? 700 : undefined
            }}
            onClick={!isCurrentPage ? handlePageClick : undefined}>
            {isFolderParent && <FolderOpenOutlined className="w-4 h-4 mr-1" />}
            {!isFolderParent && level > 0 && <MoreOutlined className="w-4 h-4 mr-1" />}
            {node.getPageName()}
          </div>
        )}
        {isParent && <div>{children}</div>}
      </div>
    );
  });
}

const PagesTree: React.FC = observer(() => {
  const { t } = useTranslation();
  const SITEMAP_ROOT_INTERNAL = SiteMapModel.SITEMAP_ROOT_INTERNAL;
  SiteBuilderModel.currentShownInPreviewGeneratedPage;

  return (
    <div className="">
      {SiteBuilderModel.generatedPages &&
      SiteBuilderModel.generatedPages.length > 0 ? (
        renderTree(SiteBuilderModel.generatedPages, SITEMAP_ROOT_INTERNAL, 0, t)
      ) : (
        <div className="text-gray-400 px-3">No pages found</div>
      )}

      <div className="px-6 mt-[24px] mb-[48px]">
        <AntdCustomButton
          appearance="important"
          className="mb-2 w-full"
          onClick={() => SiteMapModel.openManagePages()}>
          {t('Manage sitemap')}
        </AntdCustomButton>
      </div>

      <Drawer
        open={SiteMapModel.isManagePagesVisible}
        onClose={() => SiteMapModel.closeManagePages()}
        placement="left"
        width="100%"
        styles={{ body: { padding: 0 } }}
        style={{
          top: 'var(--preview-sitemap-drawer-top, 72px)',
          height: 'var(--preview-sitemap-drawer-height, calc(100vh - 162px))'
        }}
        zIndex={1200}
        destroyOnClose
        closable={false}
        mask={false}>
        <ManageSitemap onCancel={() => SiteMapModel.closeManagePages()} />
      </Drawer>

      <div className="mt-2 px-[12px]">
        <div className="flex items-start text-gray-600 text-sm">
          <InfoCircleOutlined
            className="mr-2 mt-1"
            style={{ fontSize: 16, width: 16, height: 16 }}
          />
          <BodyText style={{ marginBottom: 0 }}>
            <Trans
              i18nKey="pagesTree.sitemapDescription"
              defaults="This sitemap was generated for you based on your company data and industry. <b>You can customize your site structure at any time after completing your purchase.</b>"
              components={{ b: <b /> }}
            />
          </BodyText>
        </div>
      </div>
    </div>
  );
});

export default PagesTree;